import React, { useState } from 'react';
import { galleryImages } from '../data/mock';
import { X, ZoomIn } from 'lucide-react';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section id="gallery" className="py-24 bg-white relative overflow-hidden">
      {/* 3D Floral background decorations */}
      <div className="absolute top-0 left-1/4 w-96 h-96">
        <div className="absolute w-64 h-64 bg-lavender-100/40 rounded-full blur-3xl" />
      </div>
      <div className="absolute bottom-0 right-1/4 w-64 h-64">
        <div className="absolute w-48 h-48 bg-lilac-100/30 rounded-full blur-3xl" />
      </div>
      
      {/* Decorative floral petals */}
      <div className="absolute top-20 left-10 w-6 h-12 bg-lavender-300/25 rounded-full blur-[1px] rotate-[20deg] floral-accent" />
      <div className="absolute top-40 right-16 w-8 h-14 bg-lilac-200/30 rounded-full blur-[1px] -rotate-[30deg] floral-accent" />
      <div className="absolute bottom-32 left-20 w-5 h-10 bg-violet-200/25 rounded-full blur-[1px] rotate-[50deg] floral-accent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-lavender-600 text-sm tracking-[0.2em] uppercase mb-4" style={{ fontFamily: "'Lato', sans-serif" }}>
            Our Portfolio
          </span>
          <h2
            className="text-4xl sm:text-5xl text-grape-800 mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Event Gallery
          </h2>
          <p className="text-grape-600/70 text-lg max-w-2xl mx-auto" style={{ fontFamily: "'Lato', sans-serif" }}>
            A glimpse into the beautiful celebrations designed by Hem of Grace Events, your luxury event designer in Florida.
          </p>
        </div>
        
        {/* Large Grid Gallery */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {galleryImages.map((image, index) => {
            // Make some images span 2 columns for visual interest
            const isLarge = index === 0 || index === 5 || index === 10 || index === 15;
            
            return (
              <div
                key={image.id}
                className={`group relative cursor-pointer ${isLarge ? 'md:col-span-2 md:row-span-2' : ''}`}
                onClick={() => setSelectedImage(image)}
              >
                {/* Shadow for depth */}
                <div className="absolute inset-0 bg-lavender-300/20 rounded-2xl transform translate-y-2 translate-x-1 group-hover:translate-y-3 transition-transform duration-300" />
                
                <div className={`relative overflow-hidden rounded-2xl shadow-lg shadow-lavender-900/10 group-hover:shadow-2xl group-hover:shadow-lavender-900/20 transform group-hover:-translate-y-1 transition-all duration-500 ${isLarge ? 'aspect-square' : 'aspect-[4/5]'}`}>
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover image-hover"
                    loading="lazy"
                  />
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-grape-900/50 via-grape-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 shadow-lg">
                      <ZoomIn className="w-6 h-6 text-lavender-700" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <button
            className="absolute top-6 right-6 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
            onClick={() => setSelectedImage(null)}
            aria-label="Close lightbox"
          >
            <X className="w-7 h-7 text-white" />
          </button>
          
          <div className="relative max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <div className="absolute inset-0 bg-lavender-500/10 rounded-2xl blur-xl transform translate-y-4" />
            
            <img
              src={selectedImage.url}
              alt={selectedImage.alt}
              className="relative max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
